/**
 * Bidding price-negotiation chat between the customer and one bidding fleet (#47189).
 *
 * Built on the Fleet ↔ Driver chat contract (mobile handoff "Chat Feature — Business Logic"),
 * with the customer on the side the driver has there. REST: the Firebase token, `initiate-bid` (first
 * message), `conversations/history` (History) and offer accept. Firestore: Active chats, messages,
 * sending, offers, unread counters and live status.
 */

export enum ChatConversationStatus {
  Open = 'OPEN',
  /** Also what any missing/unknown status is treated as. */
  Closed = 'CLOSED',
}

export enum ChatSenderType {
  Fleet = 'FLEET',
  Driver = 'DRIVER',
  Customer = 'CUSTOMER',
  /** Kept for incoming messages only; an individual driver's unread key is `unreadCount.driver`. */
  IndividualDriver = 'INDIVIDUAL_DRIVER',
}

export enum ChatMessageType {
  Text = 'TEXT',
  Offer = 'OFFER',
}

/**
 * `offerStatus` of an OFFER message. Any other wire value reads as Deactivated, so an unknown
 * state never shows action buttons.
 */
export enum ChatOfferStatus {
  /** The offer on the table (the conversation's `activeOfferMessageId`). */
  Active = 'ACTIVE',
  Accepted = 'ACCEPTED',
  Rejected = 'REJECTED',
  /** Replaced by a newer offer from either side. */
  Deactivated = 'DEACTIVATED',
}

// ---------------------------------------------------------------------------------------------
// Wire: REST
// ---------------------------------------------------------------------------------------------

/** `ChatConversationResponse` of `initiate-bid` and `conversations/history`. */
export interface ChatConversationDto {
  /** Madar's own conversation uuid (the offer endpoints take the Firestore id instead). */
  id: string;
  /** Firestore doc id — every Firestore read/write goes through it. */
  firestoreConversationId: string;
  status: ChatConversationStatus;
  bidId: number;
  bidStatus: string;
  shipmentId?: number | null;
  counterpart?: { name?: string; phone?: string | null } | null;
  /** ISO string or epoch ms. */
  lastMessageAt?: string | number | null;
  lastMessagePreview?: string | null;
  lastMessageSenderType?: ChatSenderType | null;
  /** May be null on negotiation responses; the row then shows no route. */
  fromCity?: string | null;
  toCity?: string | null;
}

/** `{ items, total }` of `conversations/history`. */
export interface ChatConversationListDto {
  items: ChatConversationDto[];
  total: number;
}

/** `GET chat/firebase-token`. `expiresInSeconds` is the window to exchange it, not a session length. */
export interface ChatFirebaseTokenDto {
  firebaseToken: string;
  expiresInSeconds: number;
}

// ---------------------------------------------------------------------------------------------
// UI models (the components never see wire shapes)
// ---------------------------------------------------------------------------------------------

/** A known conversation. `status` is unknown when only the doc id is (a bid); Firestore then tells. */
export interface ChatConversationRef {
  firestoreConversationId: string;
  status?: ChatConversationStatus;
}

/** Everything the thread needs to show and find (or start) one conversation. */
export interface BidChatTarget {
  shipmentId: number;
  /** The bid being negotiated; the first message opens its conversation (`initiate-bid`). */
  bidId?: number | null;
  /** The bid's status when opened (backend: `FleetBidStatus`); the conversation doc's live one wins. */
  bidStatus?: string | null;
  counterpartName: string;
  /** Known from the bid (driver or fleet); the conversation doc confirms it once read. */
  counterpartKind?: ChatCounterpartKind;
  isVerified?: boolean;
  /** From the bid when opened from one; the conversation doc's `originalBidPrice` wins once read. */
  originalBid?: number | null;
  /** Already known (bids list / Chat logs): the thread reads it from Firestore; none = empty new chat. */
  conversation?: ChatConversationRef | null;
}

/** One row of Chat logs, from the Firestore conversation doc. */
export interface BidChatRow {
  firestoreConversationId: string;
  /** The linked bid's status (`FleetBidStatus`), from the Firestore doc or the History item. */
  bidStatus?: string | null;
  status: ChatConversationStatus;
  shipmentId: number;
  /** Localized city names. */
  fromCity?: string | null;
  toCity?: string | null;
  counterpartName: string;
  /** From the Firestore doc; unknown on History rows, which are read-only anyway. */
  counterpartKind?: ChatCounterpartKind;
  unreadCount: number;
  /** UTC ISO. */
  lastMessageAt?: string | null;
  createdAt?: string | null;
  lastMessagePreview?: string | null;
  lastMessageSenderType?: ChatSenderType | null;
}

export interface BidChatMessage {
  id: string;
  senderType: ChatSenderType;
  /** Madar user id of the sender; the customer's colleagues share the inbox, so it tells mine apart. */
  senderUserId?: string | null;
  senderName: string;
  type: ChatMessageType;
  /** Human-readable line; for an OFFER the backend keeps e.g. "Offer: 1200 SAR". */
  body: string;
  /** OFFER only: the proposed price (SAR). */
  offerPrice?: number | null;
  /** OFFER only: always true for the customer's (the bid is the opening offer). */
  isCounterOffer?: boolean;
  /** OFFER only: where the offer stands. */
  offerStatus?: ChatOfferStatus | null;
  /** UTC ISO; server time (estimated for a write that has not landed yet). */
  sentAt: string;
  /**
   * The customer's own messages only: sending (not confirmed by the server yet), sent (confirmed) or
   * failed (rejected; can be retried). There are no read receipts in this contract (§5.2).
   */
  delivery?: 'sending' | 'sent' | 'failed';
}

/** Who the customer negotiates with: a fleet, or an individual driver bidding for itself. */
export type ChatCounterpartKind = 'fleet' | 'driver';

/**
 * The thread header. Read from the conversation doc (`fleetName` / `driverName`, `isFleetVerified` /
 * `isDriverVerified`, `originalBidPrice`); whatever the doc lacks comes from the bid it was opened from.
 */
export interface BidChatHeader {
  counterpartKind: ChatCounterpartKind;
  counterpartName: string;
  isVerified?: boolean;
  originalBid?: number | null;
}

/** What the conversation doc says, live. */
export interface ChatConversationSnapshot {
  status: ChatConversationStatus;
  /** `FleetBidStatus`; null when the doc has none. */
  bidStatus: string | null;
  header: Partial<BidChatHeader>;
}

/** What the chat drawer is showing. */
export type BidChatView = { mode: 'logs' } | { mode: 'thread'; target: BidChatTarget; fromLogs?: boolean };
